import {
    ComputeStatus,
    InputUpdateMapTemplateDataset,
    MapTemplateDatasetExtended,
    useUpdateMapTemplateDatasetMutation,
} from "@biggeo/bg-server-lib/studio";
import { DragAndDropContext, DroppableColumn, Stack } from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/lib/function";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import some from "lodash/some";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { heatmapSwatchOptions } from "../../common/components/HeatMapColorMapper";
import SwitchToDatalabModal from "../../common/components/SwitchToDatalabModal";
import { useCloseModal, useOpenModal } from "../../common/redux/hooks";
import { Routes } from "../../navigation/redux/model";
import {
    useErrorToast,
    useSuccessToast,
} from "../../toaster/containers/redux/hooks";
import DatasetConfigurationContainer from "../containers/DatasetConfigurationContainer";
import { useMap } from "../mapbox/context";
import { SetDatasetContextType } from "../mapbox/context/utils/general-utils";
import { MapContextDataset } from "../mapbox/context/types";
import {
    reorderDatasetLayers,
    setDatasetLayers,
} from "../mapbox/utils/data-layers-utils";
import { getDatasetShapeColor } from "../utils/style-utils";
import DatasetCellItem from "./DatasetCellItem";
import DatasetMenu from "./DatasetMenu";
import { AddDatasetSubMenuContainer } from "../../data/containers/AddDatasetSubMenuContainer";

interface IMapLayoutLeftContent
    extends Pick<SetDatasetContextType, "updateDataset" | "reorderDatasets"> {
    readonly datasets: MapContextDataset[];
    readonly map: React.MutableRefObject<mapboxgl.Map | null>;
    readonly isMapLoaded: boolean;
    readonly mapTemplateId?: string;
    readonly setRemote: (v: readonly MapTemplateDatasetExtended[]) => void;
    readonly isGlobalTemplate?: boolean;
    readonly setLocalDataList: (v: string[]) => void;
    readonly localDataList: string[];
    readonly isSavedViewPage: boolean;
}

export const MapLayoutLeftContent = ({
    datasets,
    mapTemplateId,
    map,
    setRemote,
    setLocalDataList,
    localDataList,
    isMapLoaded,
    updateDataset,
    isSavedViewPage,
    isGlobalTemplate,
    reorderDatasets,
}: IMapLayoutLeftContent) => {
    const { dispatch: mapDispatch } = useMap();
    const successToast = useSuccessToast();
    const errorToast = useErrorToast();
    const toPage = useNavigate();

    const openModal = useOpenModal();
    const closeModal = useCloseModal();

    const { executeMutation: updateMapTemplateDatasetMutation } =
        useUpdateMapTemplateDatasetMutation();

    const dataset = useMemo(
        () =>
            pipe(
                datasets,
                A.findFirst((f) => f.isGettingStyled || f.configuration.isOpen),
                O.fold(
                    () => undefined,
                    (f) => f
                )
            ),
        [datasets]
    );

    const isGettingStyled = dataset ? dataset.isGettingStyled : false;
    const isConfigurationOpen = dataset ? dataset.configuration.isOpen : false;

    const updateMapTemplateDataset = (input: InputUpdateMapTemplateDataset) => {
        if (!isSavedViewPage) {
            updateMapTemplateDatasetMutation({
                variables: {
                    input,
                },
                onCompleted: () =>
                    successToast("Dataset Style saved successfully."),
                onError: (error) =>
                    errorToast(
                        `Error saving dataset styles: ${error.message}.`
                    ),
            });
        }
    };

    const {
        extraHeatmapOptions,
        setExtraHeatmapOptions,
        updatePolygon,
        updateColor,
        updateCustomMarker,
        updateHeatMapColor,
    } = setDatasetLayers({
        dispatch: mapDispatch,
        currentDataset: dataset,
        isSavedViewPage,
        updateMapTemplateDataset,
    });

    const handleLeftContentClick = (dataset: MapContextDataset) => {
        updateDataset({
            dataSourceId: dataset.dataSource.id,
            dataset: {
                isGettingStyled: !dataset.isGettingStyled,
            },
        });
        const { heatmap } = getDatasetShapeColor(dataset);

        const isDuplicate = some(heatmapSwatchOptions, (h) =>
            isEqual(h.swatch, heatmap.swatch)
        );

        if (!isDuplicate) {
            setExtraHeatmapOptions([heatmap]);
        }
    };

    const onSwitchToDatalabClick = () => {
        openModal({
            modalType: "new-dialog",
            component: <SwitchToDatalabModal onClose={closeModal} />,
            dialogProps: {
                variant: "centered-mid-small",
            },
        });
    };

    const manageCompute = (status?: ComputeStatus) => {
        if (status && status === ComputeStatus.cold) {
            onSwitchToDatalabClick();
        } else {
            isGlobalTemplate
                ? toPage(`${Routes.data}/available`)
                : toPage(`${Routes.data}/map-template/${mapTemplateId}`);
        }
    };

    return (
        <Stack
            gap={3}
            width="100%"
            sx={{
                padding: 3,
            }}
        >
            {mapTemplateId && !isGettingStyled && !isConfigurationOpen && (
                <AddDatasetSubMenuContainer
                    mapTemplateId={mapTemplateId}
                    existingDatasets={pipe(
                        datasets,
                        A.map((d) => d.dataSource)
                    )}
                    setRemote={setRemote}
                    localDataList={localDataList}
                    setLocalDataList={setLocalDataList}
                />
            )}
            {pipe(
                dataset,
                O.fromNullable,
                O.fold(
                    () => (
                        <DragAndDropContext
                            onDragEnd={(result) => {
                                if (!result.destination) {
                                    return;
                                }
                                const items = Array.from(datasets);
                                const [reorderedItem] = items.splice(
                                    result.source.index,
                                    1
                                );
                                items.splice(
                                    result.destination.index,
                                    0,
                                    reorderedItem
                                );

                                reorderDatasets(
                                    items.map((i) => i.dataSource.id)
                                );

                                const order = items
                                    .map((item) => ({
                                        type: item.dataSource.type ?? undefined,
                                        dataSourceId: item.dataSource.id,
                                    }))
                                    .reverse();

                                reorderDatasetLayers(order, map, isMapLoaded);
                            }}
                        >
                            <DroppableColumn droppableId="datasets">
                                <Stack
                                    sx={{
                                        "& > *": {
                                            marginY: 1,
                                        },
                                    }}
                                    width="100%"
                                >
                                    {pipe(
                                        datasets,
                                        A.mapWithIndex((idx, d) => {
                                            return (
                                                <DatasetCellItem
                                                    key={d.dataSource.id}
                                                    dataset={d}
                                                    updateDataset={
                                                        updateDataset
                                                    }
                                                    index={idx}
                                                    manageCompute={() =>
                                                        manageCompute(
                                                            d
                                                                .dataSourceConfiguration
                                                                ?.status
                                                        )
                                                    }
                                                    onItemClick={
                                                        handleLeftContentClick
                                                    }
                                                />
                                            );
                                        })
                                    )}
                                </Stack>
                            </DroppableColumn>
                        </DragAndDropContext>
                    ),
                    (currentDataset) =>
                        isGettingStyled ? (
                            <DatasetMenu
                                dataset={currentDataset}
                                onBack={(dataSourceId) =>
                                    updateDataset({
                                        dataSourceId,
                                        dataset: {
                                            isGettingStyled: false,
                                        },
                                    })
                                }
                                updateHeatMapColor={(value, dataset) =>
                                    updateHeatMapColor({
                                        value,
                                        map,
                                        isMapLoaded,
                                        dataset,
                                    })
                                }
                                extraOptions={extraHeatmapOptions}
                                updateColor={debounce(
                                    (i) =>
                                        updateColor({
                                            ...i,
                                            map,
                                            isMapLoaded,
                                        }),
                                    400
                                )}
                                updatePolygon={(i) =>
                                    updatePolygon({ ...i, map, isMapLoaded })
                                }
                                updateCustomMarker={({ marker, dataset }) =>
                                    updateCustomMarker({
                                        marker,
                                        map,
                                        isMapLoaded,
                                        dataset,
                                    })
                                }
                            />
                        ) : (
                            <DatasetConfigurationContainer
                                dataset={currentDataset}
                                updateDataset={updateDataset}
                            />
                        )
                )
            )}
        </Stack>
    );
};
